import type { PricingLaneMode } from "@/lib/salesOrgPricingClient";
import { OrgSettingsSectionManufacturerCatalogTable } from "@/components/admin/OrgSettingsSectionManufacturerCatalogTable";
import { OrgSettingsSectionPricingOverrides } from "@/components/admin/OrgSettingsSectionPricingOverrides";
import { OrgSettingsSectionSalesCatalogTable } from "@/components/admin/OrgSettingsSectionSalesCatalogTable";
import { useOrgPricingCatalogSection } from "@/components/admin/useOrgPricingCatalogSection";

export function OrgSettingsSectionPricingCatalog({ organizationId }: { organizationId: string }) {
  const {
    data,
    loading,
    msg,
    csvText,
    setCsvText,
    mfrCsvText,
    setMfrCsvText,
    busy,
    modeDraft,
    setModeDraft,
    linkDraft,
    setLinkDraft,
    applyLaneSettings,
    validateCsv,
    publishCsv,
    publishMfrCsv,
    upsertSubscriberOverride,
    deleteSubscriberOverride,
    salesCatalogItems,
    catalogTableSearch,
    setCatalogTableSearch,
    updateSalesCatalogItem,
    deleteSalesCatalogItem,
    manufacturerCatalogItems,
    mfrCatalogSearch,
    setMfrCatalogSearch,
    subscriberOverridePrefill,
    pickManufacturerRowForOverride,
  } = useOrgPricingCatalogSection(organizationId);

  const effectiveLinkedMfr =
    modeDraft === "manufacturer_inherit"
      ? linkDraft.trim() || data?.settings.linked_manufacturer_org_id || ""
      : "";
  const mfrCatalogTitle =
    modeDraft === "manufacturer_inherit"
      ? "Linked manufacturer catalog (read-only)"
      : "Your manufacturer catalog (read-only)";
  const showMfrOverridePick = modeDraft === "manufacturer_inherit" && Boolean(effectiveLinkedMfr);

  const applyDisabled =
    busy || loading || (modeDraft === "manufacturer_inherit" && !String(linkDraft).trim());
  const downloadTemplate = () => {
    const template = [
      "product_line,width_ft,length_ft,package_key,options_key,msrp,currency,effective_at,expires_at,external_product_id",
      "Elite Lofted,8,12,,,4337,USD,,",
      "Light Only,0,0,packages,,175,USD,,",
      "2x3 Window - Black,0,0,options,,105,USD,,",
    ].join("\n");
    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pricing_catalog_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <details className="rounded-xl border border-gray-200 bg-gray-50/80 dark:bg-gray-900/40 p-4">
      <summary className="flex cursor-pointer flex-wrap items-baseline gap-2 list-none marker:hidden [&::-webkit-details-marker]:hidden">
        <span aria-hidden className="text-gray-500 dark:text-gray-400">▸</span>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Training Ask — pricing lane catalog</h3>
        <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
          quarantined — click to expand
        </span>
      </summary>
      <div className="space-y-3 pt-3">
      <p className="text-xs text-gray-600 dark:text-gray-400">
        Apply <span className="font-mono">docs/sql/pricing_lane_catalog.sql</span> first. <strong>auto</strong> uses legacy
        price-book / KB expansion. <strong>sales_csv</strong> quotes only from your published CSV.{" "}
        <strong>manufacturer_inherit</strong> uses the linked manufacturer catalog plus optional subscriber overrides.
      </p>

      {msg ? (
        <div
          className={`text-xs rounded-lg px-3 py-2 border ${
            msg.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-900"
              : "bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-900"
          }`}
        >
          {msg.text}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex flex-col gap-1">
          Pricing mode
          <select
            className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-900 min-w-[200px]"
            disabled={loading || busy}
            value={modeDraft}
            onChange={(e) => setModeDraft(e.target.value as PricingLaneMode)}
          >
            <option value="auto">auto (legacy stack)</option>
            <option value="sales_csv">sales CSV (strict)</option>
            <option value="manufacturer_inherit">manufacturer inherit + overrides</option>
          </select>
        </label>
        {modeDraft === "manufacturer_inherit" ? (
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex flex-col gap-1">
            Linked manufacturer
            <select
              className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-900 min-w-[220px]"
              disabled={loading || busy}
              value={linkDraft}
              onChange={(e) => setLinkDraft(e.target.value)}
            >
              <option value="">Select manufacturer…</option>
              {(data?.manufacturer_links ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <button
          type="button"
          disabled={applyDisabled}
          onClick={() => void applyLaneSettings()}
          className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm dark:bg-gray-100 dark:text-gray-900 disabled:opacity-50"
        >
          Save lane settings
        </button>
      </div>

      <div className="text-xs text-gray-600 dark:text-gray-400">
        Active sales snapshot:{" "}
        {data?.sales_snapshot?.has_active
          ? `v${data.sales_snapshot.version ?? "?"} · ${data.sales_snapshot.row_count} rows · ${data.sales_snapshot.published_at ?? ""}`
          : data?.sales_snapshot?.tablesAvailable === false
            ? "tables not installed"
            : "none published"}
      </div>

      <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
        <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">Sales org CSV (MSRP rows)</div>
        <textarea
          className="w-full min-h-[120px] text-xs font-mono border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-900"
          placeholder={`product_line,width_ft,length_ft,msrp,external_product_id (optional)\nElite Barn,8,12,12999,MYSKU-8812`}
          value={csvText}
          disabled={busy}
          onChange={(e) => setCsvText(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={downloadTemplate}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Download template CSV
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void validateCsv()}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Validate CSV
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void publishCsv()}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            Publish snapshot
          </button>
        </div>
      </div>

      <OrgSettingsSectionSalesCatalogTable
        busy={busy}
        items={salesCatalogItems}
        search={catalogTableSearch}
        onSearchChange={setCatalogTableSearch}
        onUpdateRow={(id, patch) => updateSalesCatalogItem(id, patch)}
        onDeleteRow={(id) => deleteSalesCatalogItem(id)}
      />

      {modeDraft === "manufacturer_inherit" && !effectiveLinkedMfr ? (
        <div className="text-xs text-amber-900 dark:text-amber-100 border border-amber-300 dark:border-amber-800 rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-950/40">
          Select a linked manufacturer and save lane settings to browse their catalog and prefill subscriber overrides.
        </div>
      ) : (
        <OrgSettingsSectionManufacturerCatalogTable
          busy={busy}
          title={mfrCatalogTitle}
          items={manufacturerCatalogItems}
          search={mfrCatalogSearch}
          onSearchChange={setMfrCatalogSearch}
          showOverridePick={showMfrOverridePick}
          onPickForOverride={(row) => pickManufacturerRowForOverride(row)}
        />
      )}

      <OrgSettingsSectionPricingOverrides
        busy={busy}
        overrides={data?.overrides ?? []}
        show={
          modeDraft === "manufacturer_inherit" &&
          Boolean(linkDraft.trim() || data?.settings.linked_manufacturer_org_id)
        }
        prefill={subscriberOverridePrefill}
        onUpsert={(p) => upsertSubscriberOverride(p)}
        onDelete={(id) => deleteSubscriberOverride(id)}
      />

      <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
        <div className="text-xs font-semibold text-gray-800 dark:text-gray-200">Manufacturer catalog CSV (this org)</div>
        <p className="text-[11px] text-gray-500">Upload when this workspace org is the manufacturer. Same columns as sales CSV.</p>
        <textarea
          className="w-full min-h-[100px] text-xs font-mono border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-white dark:bg-gray-900"
          placeholder="Same format as sales CSV…"
          value={mfrCsvText}
          disabled={busy}
          onChange={(e) => setMfrCsvText(e.target.value)}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => void publishMfrCsv()}
          className="px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-950 text-sm hover:bg-amber-100"
        >
          Replace manufacturer catalog
        </button>
      </div>
      </div>
    </details>
  );
}
